export type ToolCategory =
  "Random & Pickers" | "Wheels" | "Teams & Groups" | "Classroom" | "Games & Fun";

export interface ToolMeta {
  slug: string;
  name: string;
  tagline: string;
  category: ToolCategory;
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const CATEGORIES: ToolCategory[] = [
  "Random & Pickers",
  "Wheels",
  "Teams & Groups",
  "Classroom",
  "Games & Fun",
];

export const TOOLS: ToolMeta[] = [
  {
    slug: "random-name-picker",
    name: "Random Name Picker",
    tagline: "Pick a student fairly with a fast, animated reveal.",
    category: "Random & Pickers",
    icon: "Shuffle",
    featured: true,
    keywords: [
      "random name picker",
      "student name picker",
      "name picker for classroom",
      "random name picker for classroom free",
      "free online student name picker no login",
      "random name picker without replacement",
      "name picker for teachers chromebook",
      "fair student name selector",
      "pick a student randomly online",
    ],
  },
  {
    slug: "random-student-picker",
    name: "Random Student Picker",
    tagline: "Class roster in, one lucky student out — with history.",
    category: "Random & Pickers",
    icon: "GraduationCap",
    featured: true,
    keywords: [
      "student picker",
      "classroom picker",
      "random student selector",
      "pick a student online free",
      "student name picker for teachers",
      "online class picker no signup",
      "fair classroom student picker",
      "cold call student picker online",
    ],
  },
  {
    slug: "spin-the-wheel",
    name: "Spin the Wheel",
    tagline: "A colourful decision wheel for names, activities and choices.",
    category: "Wheels",
    icon: "Disc3",
    featured: true,
    keywords: [
      "spinner wheel",
      "spin the wheel",
      "decision wheel",
      "random picker wheel",
      "spin the wheel with custom names free",
      "spin the wheel for classroom online free",
      "spin the wheel no sign up free",
      "spin the wheel teacher projector smartboard",
      "free spin the wheel for kids no download",
    ],
  },
  {
    slug: "wheel-of-names",
    name: "Wheel of Names",
    tagline: "Enter names and spin the random picker wheel to choose a winner.",
    category: "Wheels",
    icon: "Disc3",
    featured: true,
    keywords: [
      "wheel of names",
      "random name picker wheel",
      "name spinner",
      "wheel of names alternative free",
      "wheel of names no account required",
      "custom name wheel for teachers free",
      "name spinning wheel classroom",
      "random name wheel with sound effects",
      "shareable name wheel",
    ],
  },
  {
    slug: "yes-or-no-wheel",
    name: "Yes or No Wheel",
    tagline: "Instant 50/50 decision spinner wheel for quick choices.",
    category: "Wheels",
    icon: "HelpCircle",
    featured: true,
    keywords: [
      "yes or no wheel",
      "decision wheel",
      "yes no spinner",
      "yes or no wheel free online",
      "random yes or no decision maker",
      "yes no maybe wheel spinner",
      "50 50 decision wheel classroom",
      "spin yes or no online free",
    ],
  },
  {
    slug: "prize-wheel",
    name: "Prize Wheel Spinner",
    tagline: "Spin to win prizes, rewards, and classroom incentives.",
    category: "Wheels",
    icon: "Gift",
    featured: true,
    keywords: [
      "prize wheel",
      "lucky wheel",
      "giveaway wheel",
      "reward spinner",
      "free prize wheel spinner online",
      "classroom reward wheel for teachers",
      "spin the wheel for prizes free",
      "prize giveaway wheel no signup",
    ],
  },
  {
    slug: "number-wheel",
    name: "Random Number Wheel",
    tagline: "Spin a wheel of numbers 1-100 or custom numeric ranges.",
    category: "Wheels",
    icon: "Hash",
    keywords: [
      "number wheel",
      "number spinner",
      "wheel of numbers",
      "random number wheel online free",
      "spin a number wheel 1 to 100",
      "number picker wheel for classroom",
      "random number spinner online",
    ],
  },
  {
    slug: "food-wheel",
    name: "What to Eat Food Wheel",
    tagline: "Can't decide what to eat? Spin the food decision wheel!",
    category: "Wheels",
    icon: "Utensils",
    keywords: [
      "food wheel",
      "what to eat wheel",
      "lunch picker wheel",
      "dinner spinner",
      "what should I eat wheel spinner free",
      "random food decision wheel online",
      "lunch decision spinner no signup",
      "spin the wheel to decide what to eat",
    ],
  },
  {
    slug: "team-generator",
    name: "Team Generator",
    tagline: "Split any class into balanced teams in one click.",
    category: "Teams & Groups",
    icon: "Users",
    featured: true,
    keywords: [
      "team generator",
      "random teams",
      "random team generator for classroom",
      "fair group generator for students",
      "random team picker from list free",
      "group students randomly online tool free",
      "random group maker for teachers",
      "split class into groups online",
      "classroom group randomizer no login",
    ],
  },
  {
    slug: "group-maker",
    name: "Group Maker",
    tagline: "Create random groups by group count or group size.",
    category: "Teams & Groups",
    icon: "UsersRound",
    keywords: [
      "group maker",
      "random groups",
      "random group generator online free",
      "classroom group maker no login",
      "split class into random groups",
      "online group randomizer for teachers",
      "free group maker for students",
    ],
  },
  {
    slug: "partner-generator",
    name: "Partner Generator",
    tagline: "Instant random pairs, with a trio for odd numbers.",
    category: "Teams & Groups",
    icon: "Handshake",
    keywords: [
      "partner generator",
      "random pairs",
      "random partner generator classroom",
      "pair students randomly online free",
      "study partner generator for class",
      "random pair maker for teachers",
      "partner assignment generator online",
    ],
  },
  {
    slug: "random-number-generator",
    name: "Random Number Generator",
    tagline: "Numbers between any range, unique or repeating.",
    category: "Random & Pickers",
    icon: "Hash",
    keywords: [
      "random number generator",
      "number generator",
      "random number generator online free",
      "random number picker between range",
      "free random number generator no signup",
      "number randomizer online tool",
      "generate random numbers online",
    ],
  },
  {
    slug: "dice-roller",
    name: "Dice Roller",
    tagline: "Roll D4 to D100 with a satisfying tumble animation.",
    category: "Games & Fun",
    icon: "Dices",
    keywords: [
      "dice roller",
      "virtual dice",
      "online dice roller multiple dice free",
      "virtual dice roller for classroom math",
      "roll virtual dice D6 D20 online free",
      "animated online dice roller no download",
      "free virtual dice for math class",
      "digital dice roller tabletop games",
      "dice rolling simulator online",
    ],
  },
  {
    slug: "coin-flip",
    name: "Coin Flip",
    tagline: "Heads or tails, settled in a second.",
    category: "Games & Fun",
    icon: "CircleDollarSign",
    keywords: [
      "coin flip",
      "heads or tails",
      "flip a coin online free animated",
      "virtual coin flip heads or tails free",
      "online coin toss with sound no signup",
      "coin flip for classroom decision making",
      "fair online coin flip tool teachers",
      "digital heads or tails simulator",
      "coin toss online free",
    ],
  },
  {
    slug: "classroom-timer",
    name: "Classroom Timer",
    tagline: "Big projector-friendly countdown with presets.",
    category: "Classroom",
    icon: "Timer",
    featured: true,
    keywords: [
      "classroom timer",
      "countdown timer",
      "online timer for teachers",
      "free classroom timer fullscreen projector",
      "online countdown timer for teachers no signup",
      "classroom timer with alarm sound",
      "big classroom countdown timer smartboard",
      "school timer online no download",
      "visual countdown timer classroom",
    ],
  },
  {
    slug: "seating-chart",
    name: "Classroom Seating Chart",
    tagline: "Randomise seats into rows, pairs or groups — then print.",
    category: "Classroom",
    icon: "LayoutGrid",
    keywords: [
      "seating chart",
      "random seating",
      "random classroom seating chart generator free",
      "online seating chart maker for teachers",
      "free seating chart generator no signup",
      "classroom seating plan randomiser printable",
      "student seating assignment generator",
      "seating chart maker for school",
      "random seat generator classroom",
    ],
  },
  {
    slug: "classroom-helper-picker",
    name: "Classroom Helper Picker",
    tagline: "Assign class jobs like line leader and board cleaner.",
    category: "Classroom",
    icon: "Sparkles",
    keywords: [
      "classroom jobs",
      "helper picker",
      "classroom helper picker online free",
      "assign classroom jobs randomly",
      "classroom job assigner for teachers",
      "digital classroom helper picker",
      "class job picker no signup",
    ],
  },
  {
    slug: "random-letter-picker",
    name: "Random Letter Picker",
    tagline: "A–Z, vowels or consonants at the tap of a button.",
    category: "Random & Pickers",
    icon: "Type",
    keywords: [
      "random letter picker",
      "alphabet picker",
      "random letter generator online free",
      "random alphabet picker for classroom",
      "pick a random letter online",
      "letter picker for spelling games",
      "random vowel or consonant picker",
    ],
  },
  {
    slug: "random-word-picker",
    name: "Random Word Picker",
    tagline: "Spelling, everyday and adjective word banks.",
    category: "Random & Pickers",
    icon: "BookA",
    keywords: [
      "random word picker",
      "word generator",
      "random word generator online free",
      "random word picker for classroom",
      "pick a random word online no signup",
      "word picker for spelling bee",
      "random vocabulary word generator",
    ],
  },
  {
    slug: "random-choice-picker",
    name: "Random Choice Picker",
    tagline: "Let the tool settle any classroom choice.",
    category: "Random & Pickers",
    icon: "ListChecks",
    keywords: [
      "random choice picker",
      "decision maker",
      "random decision maker online free",
      "pick a random choice online",
      "random option picker no signup",
      "free online decision picker tool",
      "random choice selector classroom",
    ],
  },
  {
    slug: "yes-or-no-picker",
    name: "Yes or No Picker",
    tagline: "An instant yes, no — or maybe.",
    category: "Random & Pickers",
    icon: "CircleHelp",
    keywords: [
      "yes or no picker",
      "decision",
      "yes or no decision maker online free",
      "random yes or no answer generator",
      "yes no maybe picker online",
      "quick yes or no tool for classroom",
      "instant yes no decision tool",
    ],
  },
  {
    slug: "random-color-picker",
    name: "Random Colour Picker",
    tagline: "Pick a colour with its name and hex code.",
    category: "Random & Pickers",
    icon: "Palette",
    keywords: [
      "random color picker",
      "colour picker",
      "random colour picker online free",
      "pick a random color online",
      "random color generator no signup",
      "random hex color picker tool",
      "colour picker for art class",
    ],
  },
  {
    slug: "random-question-picker",
    name: "Random Question Picker",
    tagline: "Discussion and comprehension prompts on demand.",
    category: "Random & Pickers",
    icon: "MessageCircleQuestion",
    keywords: [
      "question picker",
      "discussion prompts",
      "random question picker for classroom",
      "random discussion question generator free",
      "question of the day picker online",
      "classroom discussion prompt generator",
      "random icebreaker question picker",
    ],
  },
  {
    slug: "lucky-wheel",
    name: "Lucky Wheel",
    tagline: "A prize wheel for rewards and treats.",
    category: "Wheels",
    icon: "Gift",
    keywords: [
      "lucky wheel",
      "prize wheel",
      "lucky wheel spinner free online",
      "spin the lucky wheel no signup",
      "free lucky wheel for giveaways",
      "lucky spin wheel classroom rewards",
      "online fortune wheel spinner",
    ],
  },
  {
    slug: "activity-wheel",
    name: "Activity Wheel",
    tagline: "Spin for the next lesson activity.",
    category: "Wheels",
    icon: "Compass",
    keywords: [
      "activity wheel",
      "lesson activity",
      "classroom activity wheel free online",
      "random lesson activity picker",
      "spin the wheel for classroom activities",
      "activity spinner for teachers no login",
      "random activity selector for class",
    ],
  },
  {
    slug: "student-wheel",
    name: "Student Wheel",
    tagline: "Your class roster on a spinning wheel.",
    category: "Wheels",
    icon: "UserRoundCheck",
    keywords: [
      "student wheel",
      "name wheel",
      "student name wheel spinner online free",
      "class name wheel picker",
      "spin the wheel with student names",
      "random student wheel for classroom",
      "student picker wheel no signup",
    ],
  },
  {
    slug: "decision-wheel",
    name: "Decision Wheel",
    tagline: "Yes, no, maybe and anything else you add.",
    category: "Wheels",
    icon: "Split",
    keywords: [
      "decision wheel",
      "choice wheel",
      "decision wheel spinner free online",
      "random decision wheel maker",
      "spin the wheel to make a decision",
      "custom decision spinner no signup",
      "free online choice wheel spinner",
    ],
  },
  {
    slug: "random-line-order",
    name: "Random Line Order",
    tagline: "Fair, printable line-up order in one shuffle.",
    category: "Teams & Groups",
    icon: "ListOrdered",
    keywords: [
      "line order",
      "queue order",
      "random line order generator for classroom",
      "random class lineup generator free",
      "fair line order picker for students",
      "classroom queue randomizer online",
      "random student lineup tool",
    ],
  },
  {
    slug: "classroom-scoreboard",
    name: "Classroom Scoreboard",
    tagline: "Track team points on the projector.",
    category: "Classroom",
    icon: "Trophy",
    featured: true,
    keywords: [
      "scoreboard",
      "team points",
      "classroom scoreboard online free",
      "digital scoreboard for teachers",
      "free team points tracker classroom",
      "online scoreboard for class games",
      "classroom point tracker no signup",
      "projector scoreboard for school",
    ],
  },
  {
    slug: "classroom-job-picker",
    name: "Classroom Job Picker",
    tagline: "Assign every classroom job in one draw.",
    category: "Classroom",
    icon: "ClipboardList",
    keywords: [
      "classroom jobs",
      "job picker",
      "classroom job picker online free",
      "assign classroom jobs randomly",
      "random class job assigner tool",
      "student job picker for teachers",
      "digital classroom helper assigner",
    ],
  },
  {
    slug: "activity-picker",
    name: "Activity Picker",
    tagline: "Warm-ups, group work and wrap-up ideas.",
    category: "Classroom",
    icon: "Lightbulb",
    keywords: [
      "activity picker",
      "lesson ideas",
      "random activity picker for classroom",
      "lesson activity idea generator free",
      "spin for a classroom activity online",
      "random warm-up activity picker",
      "lesson starter idea generator",
    ],
  },
  {
    slug: "brain-break-picker",
    name: "Brain Break Picker",
    tagline: "Quick movement and calm-down resets.",
    category: "Classroom",
    icon: "Wind",
    keywords: [
      "brain break",
      "movement break",
      "brain break ideas for classroom free",
      "random brain break picker for teachers",
      "online brain break generator no signup",
      "quick brain break activity picker",
      "classroom movement break ideas",
    ],
  },
  {
    slug: "charades-generator",
    name: "Charades Generator",
    tagline: "Animals, actions, movies and more to act out.",
    category: "Games & Fun",
    icon: "Drama",
    featured: true,
    keywords: [
      "charades",
      "acting game",
      "charades generator online free",
      "random charades word generator",
      "charades ideas for classroom",
      "free charades word picker no signup",
      "classroom charades game generator",
      "virtual charades word picker",
    ],
  },
  {
    slug: "pictionary-word-generator",
    name: "Pictionary Word Generator",
    tagline: "Easy to hard drawing words for any class.",
    category: "Games & Fun",
    icon: "Brush",
    keywords: [
      "pictionary",
      "drawing words",
      "pictionary word generator online free",
      "random pictionary word picker",
      "pictionary ideas for classroom",
      "drawing word generator no signup",
      "virtual pictionary word generator",
      "easy hard pictionary words online",
    ],
  },
  {
    slug: "would-you-rather-generator",
    name: "Would You Rather Generator",
    tagline: "Fun debate starters for the whole class.",
    category: "Games & Fun",
    icon: "Scale",
    keywords: [
      "would you rather",
      "debate starter",
      "would you rather generator online free",
      "random would you rather questions for classroom",
      "would you rather questions for kids",
      "debate starter questions generator",
      "free would you rather question picker",
      "classroom discussion starter generator",
    ],
  },
  {
    slug: "trivia-question-generator",
    name: "Trivia Question Generator",
    tagline: "Science, maths, geography and general trivia.",
    category: "Games & Fun",
    icon: "Brain",
    keywords: [
      "trivia",
      "quiz questions",
      "trivia question generator online free",
      "random trivia questions for classroom",
      "free trivia generator for teachers",
      "quiz question picker online",
      "classroom trivia generator no signup",
      "random quiz questions for students",
    ],
  },
  {
    slug: "random-challenge-generator",
    name: "Random Challenge Generator",
    tagline: "Safe, silly classroom challenges.",
    category: "Games & Fun",
    icon: "Flame",
    keywords: [
      "challenge generator",
      "class game",
      "random challenge generator online free",
      "classroom challenge ideas generator",
      "fun classroom challenge picker",
      "random dare generator for students",
      "free challenge idea generator no signup",
    ],
  },
];

export const getTool = (slug: string) => TOOLS.find((t) => t.slug === slug);

export const relatedTools = (slug: string, count = 4) => {
  const current = getTool(slug);
  const sameCategory = TOOLS.filter((t) => t.slug !== slug && t.category === current?.category);
  const others = TOOLS.filter((t) => t.slug !== slug && t.category !== current?.category);
  return [...sameCategory, ...others].slice(0, count);
};
